import FormContainer from "../../../components/formContainer/FormContainer";
import FlowFieldInput from "../../../components/input/flow/field/FlowFieldInput";
import InputWrapper from "../../../components/input/wrapper/InputWrapper";
import OverlayLoader from "../../../components/loader/OverlayLoader/OverlayLoader";
import LanguageFormSelect from "../../../components/select/custom/language/form/LanguageFormSelect";
import LocationFormSelect from "../../../components/select/custom/location/form/LocationFormSelect";
import Select from "../../../components/select/main/Select";
import { useAuthContext } from "../../../context/AuthContext";
import { useLanguageContext } from "../../../context/LanguageContext";
import EVAApiHelper from "../../../helpers/EVAApiHelper";
import { StyledInput } from "../../../styles/InputStyles";
import {
  StyledFlexColumn,
  StyledFlexItem,
  StyledFlexRow,
} from "../../../styles/LayoutStyles";
import SchedulingForm from "../../../components/schedulingForm/SchedulingForm";
import { useTheme } from "styled-components";

function VisitorPageView({
  visitor,
  visitorData,
  visitorType,
  visitorTypes,
  onVisitorTypeChange,
  onLocationChange,
  employees,
  references,
  onEmployeeIdChange,
  onLanguageChange,
  onStatusChange,
  onApprovalStatusChange,
  onReturningChange,
  onExpectedDateChange,
  onExpectedEndDateChange,
  onExpectedTimeChange,
  onFlowFieldChange,
  onParkingLocationChange,
  onLicensePlateChange,
  onScheduleChange,
  onCancel,
  onSubmit,
  submitError,
}) {
  const authContext = useAuthContext();
  const { translate, translateEntity } = useLanguageContext();
  const theme = useTheme();

  return (
  <StyledFlexColumn style={{ gap: theme.spacing.large }}>
    <OverlayLoader groups={"visitor_page_fetch"}>
      <FormContainer
        onCancel={onCancel}
        onSubmit={onSubmit}
        submitError={submitError}
        submitLoadingGroup={"visitor_page_submit"}
      >
        <StyledFlexColumn>
          {/* Location */}
          <InputWrapper
            label={translate("eva.main.validation.attributes.location_id")}
            required={true}
            error={EVAApiHelper.getResponseError("location_id", submitError)}
          >
            <LocationFormSelect
              value={visitorData.location_id}
              onChange={onLocationChange}
              useNullOption={false}
              disabled={visitor !== undefined}
            />
          </InputWrapper>

          {/* Visitor type */}
          <InputWrapper
            label={translate("eva.main.validation.attributes.visitor_type_id")}
            required={true}
            error={EVAApiHelper.getResponseError(
              "visitor_type_id",
              submitError,
            )}
          >
            <Select
              value={visitorData.visitor_type_id}
              options={visitorTypes.map((type) => ({
                value: type.id,
                label: translateEntity(type, "label"),
              }))}
              onChange={onVisitorTypeChange}
            />
          </InputWrapper>

          {/* Language */}
          <InputWrapper
            label={translate("eva.main.validation.attributes.language_id")}
            required={true}
            error={EVAApiHelper.getResponseError("language_id", submitError)}
          >
            <LanguageFormSelect
              value={visitorData.language_id}
              onChange={onLanguageChange}
            />
          </InputWrapper>

          {/* Status */}
          <InputWrapper
            label={translate("eva.main.validation.attributes.status")}
            required={true}
            error={EVAApiHelper.getResponseError("status", submitError)}
          >
            <Select
              value={visitorData.status}
              options={[
                {
                  label: translate("eva.main.visitor_statuses.expected"),
                  value: "expected",
                },
                {
                  label: translate("eva.main.visitor_statuses.signed_in"),
                  value: "signed_in",
                },
              ]}
              disabled={visitorData.fetchedStatus === "signed_in"}
              onChange={onStatusChange}
            />
          </InputWrapper>

          {/* Approval status */}
          {visitorType?.requires_approval && (
            <InputWrapper
              label={translate(
                "eva.main.validation.attributes.approval_status",
              )}
              required={true}
              error={EVAApiHelper.getResponseError(
                "approval_status",
                submitError,
              )}
            >
              <Select
                value={visitorData.approval_status}
                options={[
                  {
                    label: translate(
                      "eva.main.visitor_approval_statuses.rejected",
                    ),
                    value: "rejected",
                  },
                  {
                    label: translate(
                      "eva.main.visitor_approval_statuses.pending",
                    ),
                    value: "pending",
                  },
                  {
                    label: translate(
                      "eva.main.visitor_approval_statuses.approved",
                    ),
                    value: "approved",
                  },
                ]}
                disabled={!authContext.hasPermission("visitors.approve")}
                onChange={onApprovalStatusChange}
              />
            </InputWrapper>
          )}

          {/* Returning */}
          <InputWrapper
            label={translate("eva.main.validation.attributes.returning")}
            required
            error={EVAApiHelper.getResponseError("returning", submitError)}
          >
            <Select
              value={visitorData.returning}
              options={[
                { value: true, label: translate("eva.main.general.yes") },
                { value: false, label: translate("eva.main.general.no") },
              ]}
              onChange={onReturningChange}
            />
          </InputWrapper>

          {/* Expected date and expected end date */}
          {(visitorData.status !== "signed_in" || visitorData.returning) && (
            <StyledFlexRow>
              {(visitorData.status !== "signed_in" && !visitorData.schedule?.frequency) && (
                <StyledFlexItem>
                  <InputWrapper
                    label={translate(
                      "eva.main.validation.attributes.expected_date",
                    )}
                    required={visitorData.status !== "signed_in"}
                    error={EVAApiHelper.getResponseError(
                      "expected_date",
                      submitError,
                    )}
                  >
                    <StyledInput
                      type="date"
                      value={visitorData.expected_date || ""}
                      onChange={onExpectedDateChange}
                      min={new Date().toISOString().split("T")[0]}
                    />
                  </InputWrapper>
                </StyledFlexItem>
              )}

              {visitorData.returning && (
                <StyledFlexItem>
                  <InputWrapper
                    label={translate(
                      "eva.main.validation.attributes.expected_end_date",
                    )}
                    required={visitorData.returning}
                    error={EVAApiHelper.getResponseError(
                      "expected_end_date",
                      submitError,
                    )}
                  >
                    <StyledInput
                      type="date"
                      value={visitorData.expected_end_date || ""}
                      onChange={onExpectedEndDateChange}
                      min={
                        visitorData.expected_date ||
                        new Date().toISOString().split("T")[0]
                      }
                    />
                  </InputWrapper>
                </StyledFlexItem>
              )}
            </StyledFlexRow>
          )}

          {/* Expected time */}
          {visitorData.status !== "signed_in" && (
            <InputWrapper
              label={translate("eva.main.validation.attributes.expected_time")}
              error={EVAApiHelper.getResponseError(
                "expected_time",
                submitError,
              )}
            >
              <StyledInput
                type="time"
                value={visitorData.expected_time || ""}
                onChange={onExpectedTimeChange}
              />
            </InputWrapper>
          )}

          {/* Flow steps */}
          {visitorType?.sign_in_flow?.steps.map((step) => {
            switch (step.type) {
              case "fields":
                return step.fields.map((row, index) => {
                  const fields = row.filter((sf) => !sf.hidden_backoffice);

                  return (
                    fields.length > 0 && (
                      <StyledFlexRow key={step.id + "_row_" + index}>
                        {fields.map((stepField) => (
                          <StyledFlexItem key={stepField.id}>
                            <InputWrapper
                              label={
                                stepField.field
                                  ? translateEntity(stepField.field, "label")
                                  : translate(
                                      `eva.main.validation.attributes.${stepField.default_field}`,
                                    )
                              }
                              error={EVAApiHelper.getResponseError(
                                stepField.field
                                  ? `field_${stepField.field.id}`
                                  : stepField.default_field,
                                submitError,
                              )}
                              required={stepField.required_backoffice}
                            >
                              <FlowFieldInput
                                flowField={stepField}
                                value={
                                  visitorData[
                                    stepField.field
                                      ? `field_${stepField.field.id}`
                                      : stepField.default_field
                                  ]
                                }
                                onChange={onFlowFieldChange}
                              />
                            </InputWrapper>
                          </StyledFlexItem>
                        ))}
                      </StyledFlexRow>
                    )
                  );
                });

              case "employee":
                return (
                  <InputWrapper
                    key={step.id}
                    required={true}
                    label={translate(
                      "eva.main.validation.attributes.employee_id",
                    )}
                    error={EVAApiHelper.getResponseError(
                      "employee_id",
                      submitError,
                    )}
                  >
                    <Select
                      value={visitorData.employee_id}
                      options={employees.map((employee) => ({
                        value: employee.id,
                        label: employee.name,
                      }))}
                      onChange={onEmployeeIdChange}
                    />
                  </InputWrapper>
                );

              case "parking_location":
                return (
                  <StyledFlexColumn key={step.id}>
                    <InputWrapper
                      label={translate(
                        "eva.main.validation.attributes.parking_location_id",
                      )}
                      error={EVAApiHelper.getResponseError(
                        "parking_location_id",
                        submitError,
                      )}
                    >
                      <Select
                        value={visitorData.parking_location_id}
                        options={step.parking_locations.map((pl) => ({
                          value: pl.id,
                          label: pl.name,
                        }))}
                        onChange={onParkingLocationChange}
                        nullable={true}
                      />
                    </InputWrapper>

                    {visitorData.parking_location_id && (
                      <InputWrapper
                        label={translate(
                          "eva.main.validation.attributes.license_plate",
                        )}
                        error={EVAApiHelper.getResponseError(
                          "license_plate",
                          submitError,
                        )}
                      >
                        <StyledInput
                          type="text"
                          value={visitorData.license_plate || ""}
                          onChange={onLicensePlateChange}
                          placeholder={translate(
                            "eva.main.validation.attributes.license_plate",
                          )}
                        />
                      </InputWrapper>
                    )}
                  </StyledFlexColumn>
                );

              default:
                return "";
            }
          })}
        </StyledFlexColumn>
      </FormContainer>
    </OverlayLoader>

    {visitorData.returning && authContext.hasPermission("visitors.create.schedule") && (
      <OverlayLoader groups={"visitor_page_fetch"}>
        <SchedulingForm
          schedule={visitorData.schedule}
          onScheduleChange={onScheduleChange}
          submitError={submitError}
        />
      </OverlayLoader>
    )}

    {references?.length > 0 && (
      <OverlayLoader groups={"visitor_page_fetch"}>
        <FormContainer header={translate("eva.main.entities.references")}>
          <StyledFlexColumn>
            {references.map((reference) => (
              <StyledFlexRow key={reference.id}>
                <StyledFlexItem>
                  <InputWrapper
                    label={translate("eva.main.validation.attributes.reference")}
                  >
                    <StyledInput type="text" value={reference.reference ?? ""} disabled />
                  </InputWrapper>
                </StyledFlexItem>

                <StyledFlexItem>
                  <InputWrapper
                    label={translate("eva.main.validation.attributes.client")}
                  >
                    <StyledInput type="text" value={reference.client ?? ""} disabled />
                  </InputWrapper>
                </StyledFlexItem>

                <StyledFlexItem>
                  <InputWrapper
                    label={translate("eva.main.validation.attributes.status")}
                  >
                    <StyledInput type="text" value={reference.status ?? ""} disabled />
                  </InputWrapper>
                </StyledFlexItem>

                <StyledFlexItem>
                  <InputWrapper
                    label={translate("eva.main.validation.attributes.type")}
                  >
                    <StyledInput type="text" value={reference.type ?? ""} disabled />
                  </InputWrapper>
                </StyledFlexItem>

                <StyledFlexItem>
                  <InputWrapper
                    label={translate("eva.main.validation.attributes.adr")}
                  >
                    <StyledInput
                      type="text"
                      value={
                        reference.adr
                          ? translate("eva.main.general.yes")
                          : translate("eva.main.general.no")
                      }
                      disabled
                    />
                  </InputWrapper>
                </StyledFlexItem>

                <StyledFlexItem>
                  <InputWrapper
                    label={translate("eva.main.validation.attributes.customs")}
                  >
                    <StyledInput
                      type="text"
                      value={
                        reference.customs
                          ? translate("eva.main.general.yes")
                          : translate("eva.main.general.no")
                      }
                      disabled
                    />
                  </InputWrapper>
                </StyledFlexItem>
              </StyledFlexRow>
            ))}
          </StyledFlexColumn>
        </FormContainer>
      </OverlayLoader>
    )}
  </StyledFlexColumn>
  );
}

export default VisitorPageView;
